import React from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  ButtonGroup,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Text,
  Button,
  useToast,
} from '@chakra-ui/react';
import { T } from '@transifex/react';
import { t } from '@transifex/native';
import { useUpdateOrganizationSettingsMutation } from 'features/home/services/identity/organizationSettings';

export type AutoEnrollMode = 'enable' | 'disable';

export interface IAutoEnrollContentHubUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: AutoEnrollMode;
  'data-testid'?: string;
}

const content = {
  enable: {
    title: 'Auto-enroll Content Hub users',
    description:
      'Are you sure? While this setting is enabled, new and existing Content Hub users will be automatically enrolled in the organization ' +
      'when they interact with associated AI functionality in Content Hub.',
    buttonText: 'Enable auto-enrollment',
    buttonColor: 'green',
  },
  disable: {
    title: 'Disable Content Hub user auto-enrollment',
    description:
      'Are you sure you want to disable this feature? Any Content Hub users who are not already enrolled in the organization will no longer be automatically enrolled ' +
      'when they interact with AI functionality in Content Hub. This prevents them from using the AI functionality.\n\nDisabling this feature has no effect on users ' +
      'who have already been enrolled in the organization.',
    buttonText: 'Disable auto-enrollment',
    buttonColor: 'red',
  },
};

const AutoEnrollContentHubUsersModal = (
  props: IAutoEnrollContentHubUsersModalProps,
) => {
  const { mode = 'enable' } = props;
  const toast = useToast();
  const lessDestructiveRef = React.useRef(null);

  const { mutate: updateOrganizationSettingsMutation, isLoading } =
    useUpdateOrganizationSettingsMutation();

  const handleAutoEnrollmentChange = () => {
    const newState = mode === 'enable';

    updateOrganizationSettingsMutation({
      allowAutoEnrollmentOfChUsers: newState,
      successCb() {
        props.onClose();
        toast({
          description:
            mode === 'enable'
              ? t('Automatic enrollment of Content Hub users enabled')
              : t('Automatic enrollment of Content Hub users disabled'),
          status: 'success',
        });
      },
      errorCb() {
        props.onClose();
        toast({
          title: t('Something went wrong'),
          description: t('Try again'),
          status: 'error',
        });
      },
    });
  };

  return (
    <AlertDialog {...props} leastDestructiveRef={lessDestructiveRef}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader data-testid='AutoEnrollModal-header'>
            <T _str={content[mode].title} />
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text whiteSpace='pre-wrap'>
              <T _str={content[mode].description} />
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <ButtonGroup>
              <Button
                variant='ghost'
                onClick={props.onClose}
                isDisabled={isLoading}
                data-testid='cancel-button'
              >
                <T _str='Cancel' />
              </Button>
              <Button
                variant='solid'
                colorScheme={content[mode].buttonColor}
                onClick={handleAutoEnrollmentChange}
                isLoading={isLoading}
                ref={lessDestructiveRef}
                data-testid={`${mode}-button`}
              >
                <T _str={content[mode].buttonText} />
              </Button>
            </ButtonGroup>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default AutoEnrollContentHubUsersModal;
